import React, {useRef, useState, useCallback} from 'react';
import styled from 'styled-components';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import {ContentState, EditorState} from 'draft-js';
import {RichTextEditor} from './RichTextEditor';
import {extractMentionIds} from '../utils/insertMention';
import {mentionDecorator} from '../utils/mentionDecorator';
import {RecipientsBar} from './RecipientsBar';
import {AttachmentList} from './AttachmentList';
import {ConfirmModeSwitch} from './ConfirmModeSwitch';
import {ConfirmReplaceWithDraft} from './ConfirmReplaceWithDraft';
import {useReplySubmit} from '../hooks/useReplySubmit';
import {useCreateComment} from '../hooks/useCreateComment';
import {useAttachmentUpload} from '../hooks/useAttachmentUpload';
import {useGenerateReplyDraft} from '../hooks/useGenerateReplyDraft';

type ComposerMode = 'reply' | 'note';

const Shell = styled.div`
	margin-top: 24px;
	overflow: hidden;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`;

const TabList = styled.div`
	display: flex;
	border-bottom: 1px solid #e2e8f0;
`;
// The selected tab's underline and colour are layered onto the design-system
// Button itself, so these declarations follow the Button's own in the stylesheet
// and win. A surrounding wrapper could not colour the label, because the Button
// always sets its own `color`.
const TabButton = styled(Button)<{$active: boolean}>`
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 500;
	border-bottom: 2px solid ${p => (p.$active ? '#6259ff' : 'transparent')};
	border-radius: 0;
	color: ${p => (p.$active ? '#4f47d6' : '#64748b')};
	transition: color 0.15s;
	&:hover {
		color: ${p => (p.$active ? '#4f47d6' : '#334155')};
	}
`;

const RecipientsWrap = styled.div`
	padding: 12px 16px 0;
`;

const Body = styled.div<{$note: boolean}>`
	padding: 16px;
	background: ${p => (p.$note ? '#eeedff' : 'transparent')};
`;

const NoteHint = styled.p`
	margin: 0 0 8px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #4f47d6;
`;

const HiddenInput = styled.input`
	display: none;
`;

const ErrorText = styled.p`
	margin-top: 8px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #dc2626;
`;

const Footer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-top: 1px solid #e2e8f0;
	padding: 12px 16px;
	background: #f8fafc;
`;

const FooterLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const FooterRight = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const PaperclipIcon = styled.svg`
	height: 16px;
	width: 16px;
`;

const AiIcon = styled.svg`
	height: 14px;
	width: 14px;
	color: #6259ff;
`;

const DraftedRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 8px;
	border: 1px solid #e9d5ff;
	border-radius: 6px;
	background: #f3e8ff;
	padding: 6px 10px;
`;

const DraftedLabel = styled.span`
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	color: ${p => p.theme.sys.color.brand.primary['1']};
`;

const DraftedActions = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	margin-left: auto;
`;

interface Props {
	ticketId: string;
	requesterEmail: string;
	onSent: () => void;
	/** Used in tests to inject a pre-populated EditorState without fighting Draft.js + jsdom */
	initialEditorState?: EditorState;
	/** Used in tests to start in a specific mode */
	initialMode?: ComposerMode;
}

export function ReplyComposer({ticketId, requesterEmail, onSent, initialEditorState, initialMode}: Props) {
	const [mode, setMode] = useState<ComposerMode>(initialMode ?? 'reply');
	const [editorState, setEditorState] = useState(() => initialEditorState ?? EditorState.createEmpty(mentionDecorator));
	const [drafts, setDrafts] = useState<Record<ComposerMode, EditorState | null>>({
		reply: null,
		note: null,
	});
	const [to, setTo] = useState<string[]>([requesterEmail]);
	const [cc, setCc] = useState<string[]>([]);
	const [bcc, setBcc] = useState<string[]>([]);
	const [pendingMode, setPendingMode] = useState<ComposerMode | null>(null);
	const [confirmReplaceWithDraft, setConfirmReplaceWithDraft] = useState(false);
	const [hasDraft, setHasDraft] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	// The editor state from before the FIRST draft landed, so Revert still returns
	// the agent's own text after any number of "Try again" rounds.
	const preDraftEditorState = useRef<EditorState | null>(null);
	const replySubmit = useReplySubmit();
	const commentSubmit = useCreateComment();
	const attachUpload = useAttachmentUpload();
	const replyDraft = useGenerateReplyDraft();

	const loading = mode === 'reply' ? replySubmit.loading : commentSubmit.loading;
	const replyError = replySubmit.error;
	const noteError = commentSubmit.error;
	const submitError = mode === 'reply' ? replyError : noteError;
	const error = submitError ?? (mode === 'reply' ? attachUpload.error ?? replyDraft.error : null);

	const isEmpty = !editorState.getCurrentContent().hasText();

	// Plain text on purpose: the model returns paragraphs separated by blank lines,
	// so building content this way keeps model output off the HTML sanitizer path.
	// The composer's existing editorToHtml still handles HTML when sending.
	const runDraft = useCallback(async () => {
		if (preDraftEditorState.current === null) {
			preDraftEditorState.current = editorState;
		}
		try {
			const draftText = await replyDraft.generate(ticketId);
			setEditorState(EditorState.createWithContent(ContentState.createFromText(draftText), mentionDecorator));
			setHasDraft(true);
		} catch {
			// The hook surfaces the reason through `error`; leave the editor untouched.
			// Drop the snapshot if nothing was ever replaced, so a later successful
			// draft snapshots the agent's current text rather than a stale copy.
			if (!hasDraft) preDraftEditorState.current = null;
		}
	}, [editorState, hasDraft, replyDraft, ticketId]);

	const handleDraftWithAi = useCallback(() => {
		if (replyDraft.inFlight) return;
		// Only text the agent might lose warrants a prompt; an AI draft can be
		// re-rolled freely because Revert still holds the pre-draft state.
		if (!isEmpty && !hasDraft) {
			setConfirmReplaceWithDraft(true);
			return;
		}
		void runDraft();
	}, [replyDraft.inFlight, isEmpty, hasDraft, runDraft]);

	const confirmDraftReplace = useCallback(() => {
		setConfirmReplaceWithDraft(false);
		void runDraft();
	}, [runDraft]);

	const cancelDraftReplace = useCallback(() => setConfirmReplaceWithDraft(false), []);

	const handleRevertDraft = useCallback(() => {
		setEditorState(preDraftEditorState.current ?? EditorState.createEmpty(mentionDecorator));
		preDraftEditorState.current = null;
		setHasDraft(false);
		replyDraft.reset();
	}, [replyDraft]);

	const requestModeSwitch = useCallback(
		(target: ComposerMode) => {
			if (target === mode) return;
			if (isEmpty) {
				const restoredDraft = drafts[target];
				if (restoredDraft) {
					setEditorState(restoredDraft);
					setDrafts(prev => ({...prev, [target]: null}));
				}
				setMode(target);
			} else {
				setPendingMode(target);
			}
		},
		[mode, isEmpty, drafts]
	);

	const handleKeepDraft = useCallback(() => {
		if (!pendingMode) return;
		// Stash current editor under outgoing mode, then switch and restore any stashed draft
		setDrafts(prev => {
			const restoredDraft = prev[pendingMode];
			setEditorState(restoredDraft ?? EditorState.createEmpty(mentionDecorator));
			return {...prev, [mode]: editorState, [pendingMode]: null};
		});
		setMode(pendingMode);
		setPendingMode(null);
	}, [pendingMode, mode, editorState]);

	const handleDiscard = useCallback(() => {
		if (!pendingMode) return;
		const restoredDraft = drafts[pendingMode];
		if (restoredDraft) {
			setEditorState(restoredDraft);
			setDrafts(prev => ({...prev, [pendingMode]: null}));
		} else {
			setEditorState(EditorState.createEmpty(mentionDecorator));
		}
		// The reply content is being thrown away, so there is no longer anything for
		// Revert to restore. (Keep draft stashes it instead, so it stays revertable.)
		if (mode === 'reply') {
			preDraftEditorState.current = null;
			setHasDraft(false);
		}
		setMode(pendingMode);
		setPendingMode(null);
	}, [pendingMode, drafts, mode]);

	const cancelModeSwitch = useCallback(() => {
		setPendingMode(null);
	}, []);

	const handleFileChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files ?? []);
			files.forEach(file => void attachUpload.upload(ticketId, file));
			if (fileInputRef.current) fileInputRef.current.value = '';
		},
		[attachUpload, ticketId]
	);

	const handleSend = useCallback(async () => {
		if (mode === 'reply' && isEmpty) return;
		if (mode === 'note' && isEmpty) return;
		if (mode === 'reply' && to.length === 0) return;

		if (mode === 'reply') {
			const completedAttachments = attachUpload.attachments
				.filter(a => a.progress === 100)
				.map(({storageKey, filename, contentType, sizeBytes}) => ({
					storageKey,
					filename,
					contentType,
					sizeBytes,
				}));
			await replySubmit.submit({
				ticketId,
				editorState,
				to,
				cc: cc.length > 0 ? cc : undefined,
				bcc: bcc.length > 0 ? bcc : undefined,
				attachments: completedAttachments.length > 0 ? completedAttachments : undefined,
			});
		} else if (mode === 'note') {
			const content = editorState.getCurrentContent();
			const mentionIds = extractMentionIds(content);
			let bodyMd = content.getPlainText('\n');
			content.getBlockMap().forEach(block => {
				if (!block) return;
				block.findEntityRanges(
					char => {
						const entityKey = char.getEntity();
						if (!entityKey) return false;
						return content.getEntity(entityKey).getType() === 'MENTION';
					},
					(start, end) => {
						const entityKey = block.getEntityAt(start);
						if (!entityKey) return;
						const entity = content.getEntity(entityKey);
						const data = entity.getData() as {userId: string; name: string};
						const displayText = block.getText().slice(start, end);
						bodyMd = bodyMd.replace(displayText, `@[user:${data.userId}]`);
					}
				);
			});
			await commentSubmit.submit(ticketId, {bodyMd, mentions: mentionIds});
		}

		setEditorState(EditorState.createEmpty(mentionDecorator));
		setDrafts({reply: null, note: null});
		setTo([requesterEmail]);
		setCc([]);
		setBcc([]);
		attachUpload.clear();
		preDraftEditorState.current = null;
		setHasDraft(false);
		onSent();
	}, [mode, editorState, ticketId, requesterEmail, to, cc, bcc, replySubmit, commentSubmit, attachUpload, isEmpty, onSent]);

	return (
		<Shell>
			<TabList role="tablist">
				<TabButton
					$active={mode === 'reply'}
					emphasis={EMPHASIS.MEDIUM}
					role="tab"
					aria-selected={mode === 'reply'}
					onPress={() => requestModeSwitch('reply')}
				>
					Reply
				</TabButton>
				<TabButton
					$active={mode === 'note'}
					emphasis={EMPHASIS.MEDIUM}
					role="tab"
					aria-selected={mode === 'note'}
					onPress={() => requestModeSwitch('note')}
				>
					Internal note
				</TabButton>
			</TabList>

			{mode === 'reply' && (
				<RecipientsWrap>
					<RecipientsBar to={to} cc={cc} bcc={bcc} onToChange={setTo} onCcChange={setCc} onBccChange={setBcc} />
				</RecipientsWrap>
			)}

			<Body data-testid="composer-body" $note={mode === 'note'}>
				{mode === 'note' && <NoteHint>Only visible to your team</NoteHint>}

				<RichTextEditor
					editorState={editorState}
					onChange={setEditorState}
					placeholder={mode === 'reply' ? 'Write a reply to the customer..' : 'Write an internal note...'}
					mentionEnabled={mode === 'note'}
				/>

				{mode === 'reply' && (
					<>
						<AttachmentList attachments={attachUpload.attachments} onRemove={attachUpload.remove} />
						<HiddenInput
							ref={fileInputRef}
							type="file"
							multiple
							onChange={handleFileChange}
							aria-label="Attach files"
						/>
					</>
				)}

				{mode === 'reply' && hasDraft && (
					<DraftedRow data-testid="drafted-with-ai">
						<DraftedLabel>Drafted with AI</DraftedLabel>
						<DraftedActions>
							<Button emphasis={EMPHASIS.LOW} disabled={replyDraft.inFlight} onPress={handleRevertDraft}>
								Revert
							</Button>
							<Button emphasis={EMPHASIS.LOW} disabled={replyDraft.inFlight} onPress={handleDraftWithAi}>
								Try again
							</Button>
						</DraftedActions>
					</DraftedRow>
				)}

				{error && <ErrorText role="alert">{error}</ErrorText>}
			</Body>

			<Footer>
				<FooterLeft>
					{mode === 'reply' && (
						<LabelledButton
							emphasis={EMPHASIS.LOW}
							label="Attach files"
							onPress={() => fileInputRef.current?.click()}
						>
							<PaperclipIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13"
								/>
							</PaperclipIcon>
						</LabelledButton>
					)}
					{mode === 'reply' && (
						<Button
							emphasis={EMPHASIS.LOW}
							onPress={handleDraftWithAi}
							disabled={replyDraft.inFlight}
							aria-busy={replyDraft.inFlight}
						>
							<AiIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
								/>
							</AiIcon>
							{replyDraft.inFlight ? 'Drafting...' : 'Draft with AI'}
						</Button>
					)}
				</FooterLeft>
				<FooterRight>
					<Button emphasis={EMPHASIS.LOW} onPress={() => {}}>
						Save draft
					</Button>
					<Button
						emphasis={EMPHASIS.HIGH}
						disabled={isEmpty || loading || replyDraft.inFlight || (mode === 'reply' && to.length === 0)}
						onPress={() => void handleSend()}
					>
						{loading ? 'Sending...' : mode === 'reply' ? 'Send reply' : 'Add note'}
					</Button>
				</FooterRight>
			</Footer>

			{confirmReplaceWithDraft && (
				<ConfirmReplaceWithDraft onConfirm={confirmDraftReplace} onCancel={cancelDraftReplace} />
			)}

			{pendingMode && (
				<ConfirmModeSwitch
					fromMode={mode}
					toMode={pendingMode}
					onKeepDraft={handleKeepDraft}
					onDiscard={handleDiscard}
					onCancel={cancelModeSwitch}
				/>
			)}
		</Shell>
	);
}
